import React, { Component } from "react";
import client, { signUpload } from "@culturehq/client";
import { Form, ImageField, ModalDialog, SelectField, StringField, SubmitButton } from "@culturehq/components";

import { t, Trans } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import ChqModalDialog from "components/common/ChqModalDialog";

const FLAG_OPTIONS = [
  { value: true, label: t("Enabled") },
  { value: false, label: t("Disabled") }
];

const flagValidator = value => {
  if (value === true || value === false) {
    return null;
  }
  return "Required";
};

const POSTS_OPTIONS = [
  { value: "all", label: t("All users can create posts") },
  { value: "admins", label: t("Only admins can create posts") },
  { value: "none", label: t("Disabled") }
];

const postValidator = value => {
  if (value === "all" || value === "admins" || value === "none") {
    return null;
  }
  return "Required";
};

const EVENTS_OPTIONS = [
  { value: "all", label: t("All users can create events") },
  { value: "admins", label: t("Only admins can create events") },
  { value: "none", label: t("Disabled") }
];

const eventValidator = value => {
  if (value === "all" || value === "admins" || value === "none") {
    return null;
  }
  return "Required";
};

const SIGNIN_OPTIONS = [
  { value: "google", label: t("Sign in with Google") },
  { value: "microsoft", label: t("Sign in with Microsoft") },
  { value: "both", label: t("Sign in with both") },
  { value: "none", label: t("Can't sign in with either") }
];

const siginValidator = value => {
  if (value === "google" || value === "microsoft" || value === "both" || value === "none") {
    return null;
  }
  return "Required";
};

const getSelectedSigninOption = (googleSigninEnabled, microsoftSigninEnabled) => {
  if (googleSigninEnabled && microsoftSigninEnabled) {
    return "both";
  }
  if (googleSigninEnabled) {
    return "google";
  }
  if (microsoftSigninEnabled) {
    return "microsoft";
  }
  return "none";
};

const getSelectedEventOption = (eventsEnabled, userCreatedEventsEnabled) => {
  if (eventsEnabled && userCreatedEventsEnabled) {
    return "all";
  }
  if (eventsEnabled) {
    return "admins";
  }
  return "none";
};

const getSelectedPostOption = (postsEnabled, userCreatedPostsEnabled) => {
  if (postsEnabled && userCreatedPostsEnabled) {
    return "all";
  }
  if (postsEnabled) {
    return "admins";
  }
  return "none";
};

const getInitialValues = organization => {
  const {
    name,
    logo,
    home,
    gamificationEnabled = true,
    active = true,
    userCreatedSlackIntegrationsEnabled = false,
    userCreatedSurveysEnabled = false,
    feedbackEnabled = true,
    userCreatedInvitesEnabled = false,
    accountInfoEnabled = false,
    eventsEnabled = true,
    recognitionsEnabled = true,
    surveysEnabled = true,
    googleSigninEnabled = false,
    microsoftSigninEnabled = false,
    privacyPolicyEnabled = true,
    userCreatedEventsEnabled = true,
    postsEnabled = true,
    userCreatedPostsEnabled = true,
    isEducational = false,
    allActivityDefault = false,
    employerBranding = true,
    unsplashImages = true,
    createYourOwnStory = true,
    externalMappedIds = false,
    storiesNeedApprovalEnabled = false,
    firstNameLastNameEnabled = true
  } = organization;

  return {
    name,
    logo: logo && logo.originalUrl,
    home,
    gamificationEnabled,
    active,
    userCreatedSlackIntegrationsEnabled,
    userCreatedSurveysEnabled,
    feedbackEnabled,
    userCreatedInvitesEnabled,
    accountInfoEnabled,
    recognitionsEnabled,
    surveysEnabled,
    eventsOptions: getSelectedEventOption(eventsEnabled, userCreatedEventsEnabled),
    postsOptions: getSelectedPostOption(postsEnabled, userCreatedPostsEnabled),
    signinOptions: getSelectedSigninOption(googleSigninEnabled, microsoftSigninEnabled),
    privacyPolicyEnabled,
    userCreatedEventsEnabled,
    userCreatedPostsEnabled,
    isEducational,
    allActivityDefault,
    employerBranding,
    unsplashImages,
    createYourOwnStory,
    externalMappedIds,
    storiesNeedApprovalEnabled,
    firstNameLastNameEnabled
  };
};

class OrganizationModal extends Component {
  static defaultProps = { organization: {} };

  state = { error: null, imageProgress: null, open: false };

  handleOpen = () => {
    this.setState({ open: true });
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  handleSubmit = async ({ logo, ...others }) => {
    const { organization: { id: organizationId }, onCreate, onUpdate } = this.props;

    try {
      const values = others;
      values.googleSigninEnabled = values.signinOptions === "google" || values.signinOptions === "both";
      values.microsoftSigninEnabled = values.signinOptions === "microsoft" || values.signinOptions === "both";

      values.postsEnabled = values.postsOptions === "all" || values.postsOptions === "admins";
      values.userCreatedPostsEnabled = values.postsOptions === "all";
      values.eventsEnabled = values.eventsOptions === "all" || values.eventsOptions === "admins";
      values.userCreatedEventsEnabled = values.eventsOptions === "all";

      if (logo && logo.size) {
        values.logoUrl = await signUpload(logo, imageProgress => {
          this.setState({ imageProgress });
        });
      }

      if (organizationId) {
        const { organization } = await client.updateOrganization({ organizationId, ...values });
        onUpdate(organization);
      } else {
        const { organization } = await client.createOrganization(values);
        onCreate(organization);
      }

      this.setState({ open: false });
    } catch (error) {
      if (error.status === 422) {
        this.setState({ error });
      } else {
        throw error;
      }
    }
  };

  render() {
    const { children, organization } = this.props;
    const { error, imageProgress, open } = this.state;

    return (
      <>
        {children(this.handleOpen)}
        {open && (
          <ChqModalDialog onClose={this.handleClose} className="ManageOrganizationModal">
            <ModalDialog.Heading onClose={this.handleClose}>
              {organization ? t("Add an Organization") : t("Edit Organization")}
            </ModalDialog.Heading>
            <ModalDialog.Body>
              {error && <ErrorMessage error={error} />}
              <Form onSubmit={this.handleSubmit} initialValues={getInitialValues(organization)}>
                <StringField name="name" autoFocus required>
                  <Trans>Organization Name</Trans>
                </StringField>
                <ImageField name="logo" progress={imageProgress}>
                  <Trans>Organization Logo</Trans>
                </ImageField>
                <SelectField name="gamificationEnabled" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Enable Gamification & Rewards?</Trans>
                </SelectField>
                <SelectField
                  name="userCreatedSlackIntegrationsEnabled"
                  validator={flagValidator}
                  options={FLAG_OPTIONS}
                >
                  <Trans>Users can create Slack integrations</Trans>
                </SelectField>
                <SelectField name="userCreatedSurveysEnabled" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Users can create surveys</Trans>
                </SelectField>
                <SelectField name="feedbackEnabled" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Users can give feedback</Trans>
                </SelectField>
                <SelectField name="userCreatedInvitesEnabled" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Users can invite other users</Trans>
                </SelectField>
                <SelectField name="accountInfoEnabled" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Users see the account information page</Trans>
                </SelectField>
                <SelectField name="recognitionsEnabled" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Recognition feature enabled</Trans>
                </SelectField>
                <SelectField name="surveysEnabled" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Survey feature enabled</Trans>
                </SelectField>
                <SelectField name="signinOptions" validator={siginValidator} options={SIGNIN_OPTIONS}>
                  <Trans>User sign in options</Trans>
                </SelectField>
                <SelectField name="privacyPolicyEnabled" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Users must accept the CultureHQ privacy policy</Trans>
                </SelectField>
                <SelectField name="eventsOptions" validator={eventValidator} options={EVENTS_OPTIONS}>
                  <Trans>Enable events</Trans>
                </SelectField>
                <SelectField name="postsOptions" validator={postValidator} options={POSTS_OPTIONS}>
                  <Trans>Enable posts</Trans>
                </SelectField>
                <SelectField name="isEducational" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Is an Educational Organization?</Trans>
                </SelectField>
                <SelectField
                  name="allActivityDefault"
                  validator={flagValidator}
                  options={[
                    { value: false, label: t("For You") },
                    { value: true, label: t("All Activity") }
                  ]}
                >
                  <Trans>Default view for activity feed</Trans>
                </SelectField>
                <SelectField name="employerBranding" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Employer branding</Trans>
                </SelectField>
                <SelectField name="unsplashImages" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Unsplash images</Trans>
                </SelectField>
                <SelectField name="createYourOwnStory" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Create your own story</Trans>
                </SelectField>
                <SelectField name="externalMappedIds" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>External job mappings</Trans>
                </SelectField>
                <SelectField name="storiesNeedApprovalEnabled" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>Stories need approval for internal view</Trans>
                </SelectField>
                <SelectField name="firstNameLastNameEnabled" validator={flagValidator} options={FLAG_OPTIONS}>
                  <Trans>First Name and Last Name fields</Trans>
                </SelectField>
                <SelectField
                  name="active"
                  validator={flagValidator}
                  options={[
                    { value: true, label: t("Enabled") },
                    { value: false, label: t("Inactive") }
                  ]}
                >
                  <Trans>Enabled</Trans>
                </SelectField>
                <SubmitButton primary>
                  {submitting => (submitting ? t("Submitting...") : t("Save"))}
                </SubmitButton>
              </Form>
            </ModalDialog.Body>
          </ChqModalDialog>
        )}
      </>
    );
  }
}

export default OrganizationModal;
